import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { Stage, ChapterTag, MaskedLines } from "@/components/Reveal";
import { Arch, PulseGlow } from "@/components/Motifs";

const FRAGS = [
  { id: "message-nw", x: 5, y: 12, w: 150, t: "msg", d: 3.4, r: -4 },
  { id: "feed-ne", x: 80, y: 8, w: 170, h: 210, t: "feed", d: 4.2, r: 3 },
  { id: "pill-upper", x: 58, y: 20, w: 150, t: "pill", d: 2.8, r: 2 },
  { id: "avatar-upper", x: 24, y: 30, w: 150, t: "ava", d: 3.1, r: -2 },
  { id: "seen", x: 70, y: 34, w: 120, t: "word", text: "seen 9:41", d: 3.6, r: -3 },
  { id: "feed-west", x: 12, y: 48, w: 160, h: 180, t: "feed", d: 4.6, r: 2 },
  { id: "message-top", x: 44, y: 10, w: 130, t: "msg", d: 3.0, r: 5 },
  { id: "message-east", x: 88, y: 52, w: 140, t: "msg", d: 3.8, r: -5 },
  { id: "typing", x: 34, y: 58, w: 120, t: "word", text: "typing…", d: 2.6, r: 2 },
  { id: "avatar-lower", x: 62, y: 66, w: 150, t: "ava", d: 3.3, r: -2 },
  { id: "pill-west", x: 8, y: 72, w: 130, t: "pill", d: 4.0, r: 4 },
  { id: "feed-se", x: 78, y: 74, w: 160, h: 190, t: "feed", d: 4.4, r: -3 },
  { id: "suggestion", x: 48, y: 82, w: 130, t: "word", text: "you might like", d: 3.2, r: 3 },
  { id: "message-sw", x: 28, y: 78, w: 140, t: "msg", d: 3.7, r: -4 },
  { id: "pill-east", x: 92, y: 22, w: 110, t: "pill", d: 2.9, r: 6 },
  { id: "notifications", x: 16, y: 6, w: 120, t: "word", text: "4 new notifications", d: 3.5, r: -2 },
  { id: "feed-center", x: 52, y: 44, w: 150, h: 170, t: "feed", d: 4.1, r: 2 },
  { id: "pill-center", x: 38, y: 36, w: 120, t: "pill", d: 3.0, r: -6 },
  { id: "message-bottom", x: 66, y: 86, w: 130, t: "msg", d: 3.9, r: 2 },
  { id: "online", x: 2, y: 34, w: 110, t: "word", text: "online now", d: 2.7, r: -3 },
];

const HERO_LINES = ["We have never had more ways", "to reach each other."];
const NOISE_RANGES = {
  opening: [0.3, 0.44],
  question: [0.46, 0.55, 0.64, 0.74],
};

const Frag = ({ f, i, progress }) => {
  const t0 = -0.1 + (i % 11) * 0.02;
  const k0 = 0.56 + (i % 5) * 0.03;
  const opacity = useTransform(progress, [t0, t0 + 0.07, k0, k0 + 0.14], [0, 1, 1, 0]);
  const y = useTransform(progress, [t0, k0 + 0.14], [30 + (i % 4) * 10, -36 - (i % 3) * 16]);
  return (
    <motion.div
      className={`th-frag th-frag-${f.t} ${i % 3 === 0 ? "th-frag-far" : ""}`}
      data-testid={`noise-fragment-${i}`}
      style={{ left: `${f.x}%`, top: `${f.y}%`, width: f.w, height: f.h, opacity, y, rotate: f.r }}
    >
      <div className="th-frag-inner" style={{ animationDuration: `${f.d}s` }}>
        {f.t === "word" && <span>{f.text}</span>}
        {f.t === "msg" && (
          <>
            <i style={{ width: "72%" }} />
            <i style={{ width: "46%" }} />
          </>
        )}
        {f.t === "feed" && (
          <>
            <b className="th-frag-media" />
            <i style={{ width: "58%" }} />
            <i style={{ width: "80%" }} />
          </>
        )}
        {(f.t === "ava" || f.t === "pill") && (
          <>
            <b className="th-frag-dot" />
            <i style={{ width: f.t === "ava" ? 70 : 44 }} />
          </>
        )}
      </div>
    </motion.div>
  );
};

export default function Noise() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const blur = useTransform(scrollYProgress, [0.55, 0.8], [0, 2.5]);
  const filter = useMotionTemplate`blur(${blur}px)`;
  const mX = useTransform(scrollYProgress, [0.6, 0.88], ["0vw", "-9vw"]);
  const mY = useTransform(scrollYProgress, [0.6, 0.88], ["0vh", "-16vh"]);
  const mW = useTransform(scrollYProgress, [0.6, 0.9], [150, 118]);
  const mH = useTransform(scrollYProgress, [0.6, 0.9], [64, 180]);
  const mR = useTransform(scrollYProgress, [0.6, 0.9], [14, 59]);
  const mRad = useMotionTemplate`${mR}px ${mR}px 12px 12px`;
  const mOp = useTransform(scrollYProgress, [0.03, 0.1, 0.88, 0.95], [0, 1, 1, 0]);
  const mBars = useTransform(scrollYProgress, [0.66, 0.78], [1, 0]);
  const mBarsScale = useTransform(scrollYProgress, [0.66, 0.78], [1, 0.15]);
  const mBg = useTransform(scrollYProgress, [0.66, 0.8], [0.62, 0]);
  const mBgT = useMotionTemplate`rgba(255,255,255,${mBg})`;
  const archOp = useTransform(scrollYProgress, [0.84, 0.94], [0, 1]);
  const archScale = useTransform(scrollYProgress, [0.84, 1], [0.85, 1]);

  return (
    <section id="scene-noise" ref={ref} className="th-scene" style={{ height: "240vh" }} data-testid="scene-noise">
      <div className="th-sticky">
        <ChapterTag index="01" name="The Noise" />
        <div className="th-foreshadow" aria-hidden="true">
          <Arch style={{ height: "120vh" }} sw={1} stroke="var(--ink)" />
        </div>
        <motion.div className="absolute inset-0" style={{ filter }}>
          {FRAGS.map((f, i) => (
            <Frag key={f.id} f={f} i={i} progress={scrollYProgress} />
          ))}
        </motion.div>
        <motion.div
          className="th-morph"
          style={{ x: mX, y: mY, width: mW, height: mH, borderRadius: mRad, opacity: mOp, background: mBgT }}
          data-testid="noise-morph"
        >
          <div className="th-morph-drift">
            <motion.div className="th-morph-bars" style={{ opacity: mBars, scaleY: mBarsScale, originY: 0 }}>
              <i style={{ width: "72%" }} />
              <i style={{ width: "46%" }} />
            </motion.div>
          </div>
        </motion.div>
        <Stage progress={scrollYProgress} range={NOISE_RANGES.opening} loadIn>
          <MaskedLines
            className="th-statement th-halo"
            delay={0.5}
            lines={HERO_LINES}
          />
        </Stage>
        <Stage progress={scrollYProgress} range={NOISE_RANGES.question}>
          <h2 className="th-statement th-halo">
            Why does meaningful connection
            <br />
            still feel so hard?
          </h2>
        </Stage>
        <motion.div className="absolute inset-0 flex items-center justify-center" style={{ opacity: archOp }}>
          <motion.div className="relative flex flex-col items-center" style={{ scale: archScale }}>
            <Arch className="w-[110px] md:w-[140px]" stroke="var(--ink-soft)" sw={1.4} data-testid="noise-quiet-arch" />
            <PulseGlow className="absolute -bottom-1" size={9} style={{ left: "50%", marginLeft: -4 }} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
